import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { Mail, Phone, MapPin } from "lucide-react";
import BookingModal from "@/components/BookingModal";

const SERVICE_OPTIONS = [
  "Not sure yet",
  "Google Ads",
  "Meta Advertising",
  "Social Media Management",
  "Content Creation",
  "Website Design",
  "Branding",
  "SEO",
  "Email Marketing",
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", service: "Not sure yet", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/enquiries", { ...form, source: "contact-page" });
      toast.success("Thanks, we'll be in touch shortly.");
      setDone(true);
      setForm({ name: "", email: "", phone: "", company: "", service: "Not sure yet", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please email aimee@onboard.nz.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO title="Contact" description="Say hi to the Onboard Marketing team. We'd love to hear about your business and how we can help." />
      <section className="pt-16 pb-16 lg:pt-24">
        <div className="ob-container">
          <h1 className="ob-h1 max-w-5xl">Let's see if we're the right fit.</h1>
          <p className="ob-lead mt-8 max-w-3xl">
            Whether you already know what you need or you're just looking for another perspective, we'd love to hear about your business. Tell us a little below and we'll come back with next steps, usually within a business day.
          </p>
        </div>
      </section>

      <section className="pb-32">
        <div className="ob-container grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5 space-y-10">
            <div>
              <div className="ob-eyebrow mb-3">Email</div>
              <a href="mailto:aimee@onboard.nz" className="text-[22px] font-light inline-flex items-center gap-3" style={{ color: "var(--ob-ink)" }} data-testid="contact-email">
                <Mail size={18} strokeWidth={1.5} /> aimee@onboard.nz
              </a>
            </div>
            <div>
              <div className="ob-eyebrow mb-3">Phone</div>
              <a href="tel:+64210314301" className="text-[22px] font-light inline-flex items-center gap-3" style={{ color: "var(--ob-ink)" }} data-testid="contact-phone">
                <Phone size={18} strokeWidth={1.5} /> +64 210 314 301
              </a>
            </div>
            <div>
              <div className="ob-eyebrow mb-3">Offices</div>
              <div className="text-[17px] font-light flex items-start gap-3" style={{ color: "var(--ob-ink)" }}>
                <MapPin size={18} strokeWidth={1.5} className="mt-1" />
                <span>Auckland &amp; Tauranga, New Zealand</span>
              </div>
            </div>
            <div className="pt-6 border-t border-[var(--ob-border)]">
              <div className="ob-eyebrow mb-3">Prefer to book a time?</div>
              <button
                type="button"
                onClick={() => setBookingOpen(true)}
                className="ob-btn ob-btn-primary"
                data-testid="contact-book"
              >
                Book a strategy call
              </button>
            </div>
          </div>

          <form onSubmit={submit} className="lg:col-span-7 space-y-5" data-testid="contact-form">
            {done && (
              <div className="p-5 rounded-2xl bg-[var(--ob-bg-panel)] text-[15px]" data-testid="contact-success">
                Thanks, your enquiry has been received. We'll be in touch shortly.
              </div>
            )}
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="ob-eyebrow block mb-2">Name</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className="w-full h-14 px-5 rounded-full border border-[var(--ob-border)] bg-white text-[16px]" data-testid="contact-name" />
              </div>
              <div>
                <label className="ob-eyebrow block mb-2">Email</label>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email address" className="w-full h-14 px-5 rounded-full border border-[var(--ob-border)] bg-white text-[16px]" data-testid="contact-email-input" />
              </div>
              <div>
                <label className="ob-eyebrow block mb-2">Phone</label>
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Optional" className="w-full h-14 px-5 rounded-full border border-[var(--ob-border)] bg-white text-[16px]" data-testid="contact-phone-input" />
              </div>
              <div>
                <label className="ob-eyebrow block mb-2">Company</label>
                <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Optional" className="w-full h-14 px-5 rounded-full border border-[var(--ob-border)] bg-white text-[16px]" data-testid="contact-company" />
              </div>
            </div>
            <div>
              <label className="ob-eyebrow block mb-2">What are you interested in?</label>
              <select value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} className="w-full h-14 px-5 rounded-full border border-[var(--ob-border)] bg-white text-[16px]" data-testid="contact-service">
                {SERVICE_OPTIONS.map((o) => (<option key={o} value={o}>{o}</option>))}
              </select>
            </div>
            <div>
              <label className="ob-eyebrow block mb-2">Message</label>
              <textarea required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tell us a bit about your business and what you're looking to achieve." rows={6} className="w-full p-5 rounded-3xl border border-[var(--ob-border)] bg-white text-[16px] resize-none" data-testid="contact-message" />
            </div>
            <button disabled={submitting} type="submit" className="ob-btn ob-btn-primary h-12 px-8" data-testid="contact-submit">
              {submitting ? "Sending..." : "Send enquiry"}
            </button>
          </form>
        </div>
      </section>

      <BookingModal open={bookingOpen} onClose={() => setBookingOpen(false)} />
    </div>
  );
}
